import { Link } from 'react-router';
import { ArrowRight } from 'lucide-react';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

export default function NotFound() {
  useDocumentTitle('Page Not Found — Straton Group');

  return (
    <main id="main-content" className="relative min-h-screen bg-bg-primary overflow-hidden flex items-center justify-center">
      {/* ── Background mesh wave (left side) ── */}
      <div className="absolute inset-0 pointer-events-none">
        <svg
          className="absolute bottom-0 left-0 w-full h-[60%] opacity-[0.08]"
          viewBox="0 0 1440 600"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="meshGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#2A2A2A" />
              <stop offset="100%" stopColor="#0A0A0A" />
            </linearGradient>
          </defs>
          {/* Wireframe terrain lines */}
          {Array.from({ length: 12 }).map((_, i) => (
            <path
              key={`wave-${i}`}
              d={`M0,${350 + i * 22} Q360,${300 + i * 18} 720,${340 + i * 20} T1440,${360 + i * 16}`}
              fill="none"
              stroke="#2A2A2A"
              strokeWidth="0.5"
            />
          ))}
          {/* Vertical grid lines */}
          {Array.from({ length: 20 }).map((_, i) => (
            <line
              key={`vline-${i}`}
              x1={i * 80}
              y1="300"
              x2={i * 80 - 200}
              y2="600"
              stroke="#2A2A2A"
              strokeWidth="0.3"
            />
          ))}
        </svg>
      </div>

      {/* ── Corner brackets ── */}
      <div className="absolute top-24 left-8 lg:left-16 w-6 h-6 border-l border-t border-text-muted/30" />
      <div className="absolute top-24 right-8 lg:right-16 w-6 h-6 border-r border-t border-text-muted/30" />
      <div className="absolute bottom-24 right-8 lg:right-16 w-6 h-6 border-r border-b border-text-muted/30" />

      {/* ── Crosshair markers ── */}
      <div className="absolute top-32 right-[15%] text-text-muted/20 text-lg">+</div>
      <div className="absolute top-[20%] right-[8%] text-text-muted/20 text-sm">+</div>
      <div className="absolute bottom-[30%] left-[12%] text-text-muted/20 text-xs">+</div>

      {/* ── Left readout ── */}
      <div className="absolute left-8 lg:left-16 top-1/2 -translate-y-1/2 hidden md:block">
        <div className="space-y-5">
          <div>
            <div className="text-[9px] uppercase tracking-[0.2em] text-text-muted mb-1">System Status</div>
            <div className="w-8 h-px bg-text-muted/30 mb-1" />
            <div className="text-[10px] uppercase tracking-[0.15em] text-text-secondary">Offline</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.2em] text-text-muted mb-1">Node</div>
            <div className="w-8 h-px bg-text-muted/30 mb-1" />
            <div className="text-[10px] uppercase tracking-[0.15em] text-text-secondary">Unknown</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.2em] text-text-muted mb-1">Code</div>
            <div className="w-8 h-px bg-text-muted/30 mb-1" />
            <div className="text-[10px] uppercase tracking-[0.15em] text-text-secondary">404</div>
          </div>
        </div>
      </div>

      {/* ── Right readout ── */}
      <div className="absolute right-8 lg:right-16 top-1/2 -translate-y-1/2 hidden md:block text-right">
        <div className="text-[9px] uppercase tracking-[0.2em] text-text-muted mb-3">Coordinates</div>
        <div className="space-y-1.5">
          <div className="text-[10px] tracking-[0.1em] text-text-secondary font-mono">X: -38.8985</div>
          <div className="text-[10px] tracking-[0.1em] text-text-secondary font-mono">Y: 77.0953</div>
          <div className="text-[10px] tracking-[0.1em] text-text-secondary font-mono">Z: 0.0000</div>
        </div>
        <div className="w-12 h-px bg-text-muted/30 mt-3 ml-auto" />
        <div className="w-6 h-px bg-text-muted/30 mt-1.5 ml-auto" />
      </div>

      {/* ── Center content ── */}
      <div className="relative z-10 text-center px-6">
        {/* Error label */}
        <div className="flex items-center justify-center gap-4 mb-6">
          <span className="text-[10px] uppercase tracking-[0.2em] text-text-muted">
            Error 404
          </span>
          <div className="w-12 h-px bg-text-muted/40" />
        </div>

        {/* Giant 404 */}
        <div className="flex items-center justify-center gap-0 mb-6 select-none">
          <span className="text-[120px] sm:text-[160px] md:text-[200px] lg:text-[240px] font-light text-text-primary leading-none tracking-tighter">
            4
          </span>
          {/* Diagonal-cut zero */}
          <span className="relative text-[120px] sm:text-[160px] md:text-[200px] lg:text-[240px] font-light text-text-primary leading-none tracking-tighter">
            <span
              className="inline-block"
              style={{
                clipPath: 'polygon(0 0, 55% 0, 45% 100%, 0 100%)',
              }}
            >
              0
            </span>
            <span
              className="inline-block absolute left-0 top-0"
              style={{
                clipPath: 'polygon(55% 0, 100% 0, 100% 100%, 45% 100%)',
              }}
            >
              0
            </span>
          </span>
          <span className="text-[120px] sm:text-[160px] md:text-[200px] lg:text-[240px] font-light text-text-primary leading-none tracking-tighter">
            4
          </span>
        </div>

        {/* Route not found */}
        <h1 className="text-lg md:text-xl font-semibold uppercase tracking-[0.3em] text-text-primary mb-5">
          Route Not Found
        </h1>

        {/* Subtitle */}
        <p className="text-sm text-text-secondary max-w-md mx-auto mb-10 leading-relaxed">
          The page you are looking for does not exist or has been moved to another location.
        </p>

        {/* CTA */}
        <Link
          to="/"
          className="inline-flex items-center gap-3 px-8 py-3.5 border border-text-muted/40 text-text-primary text-xs font-medium uppercase tracking-widest hover:border-text-muted hover:bg-text-muted/5 transition-all duration-300"
        >
          <span>Return to Home</span>
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </main>
  );
}
